import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import ChangePassword from "@/pages/ChangePassword";
import Settings from "@/pages/Settings";
import BusinessUnits from "@/pages/BusinessUnits";
import BusinessUnitDetail from "@/pages/BusinessUnitDetail";
import TemplateBuilder from "@/pages/TemplateBuilder";
import TemplateView from "@/pages/TemplateView";
import Reviews from "@/pages/Reviews";
import ReviewWorkflow from "@/pages/ReviewWorkflow";
import Notifications from "@/pages/Notifications";
import Approvals from "@/pages/Approvals";
import Audit from "@/pages/Audit";
import Activity from "@/pages/Activity";
import PolicyEditor from "@/pages/PolicyEditor";
import MyPolicies from "@/pages/MyPolicies";
import PlatformOrganogram from "@/pages/PlatformOrganogram";
import Global from "@/pages/Global";
import Reports from "@/pages/Reports";
import { landingFor } from "@/lib/landing";
import { useAuth } from "@/contexts/AuthContext";

function LandingRedirect() {
  const { user } = useAuth();
  return <Navigate to={landingFor(user)} replace />;
}

function Page({ children }) {
  return (
    <ProtectedRoute>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
}

function SuperAdminPage({ children }) {
  return (
    <ProtectedRoute roles={["super_admin"]}>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
}

function BuMgtPage({ children }) {
  return (
    <ProtectedRoute roles={["super_admin", "bu_owner", "bu_manager"]}>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
}

function ApproverPage({ children }) {
  return (
    <ProtectedRoute roles={["super_admin", "bu_owner"]}>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrandingProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/change-password" element={
            <ProtectedRoute><ChangePassword /></ProtectedRoute>
          } />
          <Route path="/" element={<LandingRedirect />} />
          <Route path="/dashboard" element={<LandingRedirect />} />
          <Route path="/settings" element={<SuperAdminPage><Settings /></SuperAdminPage>} />
          <Route path="/global" element={<SuperAdminPage><Global /></SuperAdminPage>} />
          <Route path="/reports" element={<SuperAdminPage><Reports /></SuperAdminPage>} />
          <Route path="/business-units" element={<Page><BusinessUnits /></Page>} />
          <Route path="/business-units/:id" element={<Page><BusinessUnitDetail /></Page>} />
          <Route path="/templates/:id/edit" element={<BuMgtPage><TemplateBuilder /></BuMgtPage>} />
          <Route path="/templates/:id/view" element={<Page><TemplateView /></Page>} />
          <Route path="/reviews" element={<Page><Reviews /></Page>} />
          <Route path="/reviews/:id" element={<Page><ReviewWorkflow /></Page>} />
          <Route path="/notifications" element={<Page><Notifications /></Page>} />
          <Route path="/inbox" element={<Page><Activity /></Page>} />
          <Route path="/activity" element={<Page><Activity /></Page>} />
          <Route path="/approvals" element={<ApproverPage><Approvals /></ApproverPage>} />
          <Route path="/audit" element={<SuperAdminPage><Audit /></SuperAdminPage>} />
          <Route path="/policies/:id" element={<Page><PolicyEditor /></Page>} />
          <Route path="/my-policies" element={<Page><MyPolicies /></Page>} />
          <Route path="/organogram" element={<BuMgtPage><PlatformOrganogram /></BuMgtPage>} />
          <Route path="*" element={<LandingRedirect />} />
        </Routes>
      </BrowserRouter>
      </BrandingProvider>
    </AuthProvider>
  );
}

export default App;
